/**
 * farmerApi.js — centralised API service for the Farmer Dashboard
 * All fetch logic lives here; components just call these functions.
 */

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/* ─── helpers ─────────────────────────────────────────────────── */
async function get(path) {
  const res = await fetch(`${BASE}${path}`);
  if (!res.ok) throw new Error(`HTTP ${res.status} — ${path}`);
  return res.json();
}

/* ─── Active Crops ────────────────────────────────────────────── */
/**
 * Returns an array of crop objects shaped for the ActiveCrops card.
 * Pulls from /api/india-crops and maps cropDetails into a display-ready list.
 */
export async function fetchActiveCrops() {
  const data = await get('/api/india-crops');

  const { cropDetails = {}, legend = {} } = data;
  const icons   = legend.icons  || {};
  const colors  = legend.colors || [];
  const cropList = legend.crops || [];

  // Map each crop in cropDetails to a display row
  const crops = Object.entries(cropDetails).map(([name, detail], idx) => {
    // Health score: derive from NDVI (0–1 → 0–100) or fall back to random 70-95
    const ndvi   = parseFloat(detail.ndvi) || 0;
    const health = ndvi > 0
      ? Math.min(100, Math.round(ndvi * 120))          // scale NDVI to %
      : Math.floor(70 + Math.random() * 25);

    // Pick a consistent color from the legend palette
    const colorIdx = cropList.indexOf(name);
    const color    = colorIdx >= 0 ? colors[colorIdx] : colors[idx % colors.length] || '#28a745';

    // Field label: use first state that grows this crop
    const field = Array.isArray(detail.states) && detail.states.length > 0
      ? detail.states[0]
      : 'India';

    return {
      name,
      variety:  detail.season ? `${detail.season} crop` : 'Mixed variety',
      field,
      health,
      color,
      icon:     icons[name] || '🌾',
      season:   detail.season   || '—',
      water:    detail.waterSource || '—',
      ndvi:     detail.ndvi     || '—',
      moisture: detail.moisture || '—',
      status:   detail.health   || 'Good',
      area:     detail.area     || 0,
      production: detail.production || 0,
    };
  });

  // Sort by health descending so healthiest crops appear first
  return crops.sort((a, b) => b.health - a.health);
}

/* ─── Weather ─────────────────────────────────────────────────── */
/**
 * Fetches current weather for a city name.
 * Returns { city, current: { temp_c, humidity, wind_kph, precip_mm, condition } }
 */
export async function fetchCurrentWeather(city) {
  return get(`/api/india/weather/dynamic?q=${encodeURIComponent(city)}`);
}

/**
 * Fetches N-day forecast for a city.
 * Returns { city, forecast: { forecastday: [...] } }
 */
export async function fetchForecast(city, days = 7) {
  return get(`/api/india/forecast?q=${encodeURIComponent(city)}&days=${days}`);
}

/* ─── Geolocation helper ──────────────────────────────────────── */
/**
 * Resolves the browser's GPS coordinates to a city name via Nominatim.
 * Returns a string city name, or 'New Delhi' as fallback.
 */
export function detectCity() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve('New Delhi');

    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.latitude}&lon=${coords.longitude}&zoom=10`,
            { headers: { 'User-Agent': 'AgroFarming-App' } }
          );
          const data = await res.json();
          const city =
            data.address?.city        ||
            data.address?.town        ||
            data.address?.village     ||
            data.address?.county      ||
            data.address?.state_district ||
            'New Delhi';
          resolve(city);
        } catch {
          resolve('New Delhi');
        }
      },
      () => resolve('New Delhi')
    );
  });
}
